/**
 * CoveragePicker.js
 * Escolha da carteirinha de convênio de uma pessoa, com cadastro na hora.
 *
 * Presentacional: recebe as carteirinhas já carregadas (usePatientInsurances)
 * e devolve o id escolhido. Sem nenhuma carteirinha ativa, mostra o
 * PatientInsuranceForm em modo compacto no lugar do select; com carteirinhas,
 * oferece o link "Nova carteirinha" para cadastrar sem sair do contexto.
 * Usado pelo bloco "Cobertura" do drawer e pelo diálogo do painel de detalhe.
 */

import React, { useEffect, useRef, useState } from 'react';
import PatientInsuranceForm from '../../../../../components/insurance/PatientInsuranceForm';
import { insuranceCardLabel } from '../../utils/billingType';
import drawerStyles from './AppointmentDrawer.module.css';
import styles from './CoveragePicker.module.css';

/** 'YYYY-MM-DD' -> 'DD/MM/YYYY' */
function formatDay(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/**
 * @param {Object} props
 * @param {number|string} props.patientId
 * @param {Array} props.providers - CompanyConfig.InsuranceProviders (operadoras ativas)
 * @param {Array} props.cards - carteirinhas ATIVAS da pessoa
 * @param {boolean} props.loading
 * @param {number|string|null} props.value - id da carteirinha escolhida
 * @param {Function} props.onChange - (cardId|null) => void
 * @param {Function} props.onCardCreated - (card) => void, carteirinha cadastrada na hora
 * @param {string} [props.error] - mensagem de validação (carteirinha obrigatória)
 * @param {boolean} [props.disabled]
 */
export default function CoveragePicker({
  patientId,
  providers = [],
  cards = [],
  loading = false,
  value,
  onChange,
  onCardCreated,
  error,
  disabled = false,
}) {
  const [showForm, setShowForm] = useState(false);
  const previousValue = useRef(null);
  const hasCards = cards.length > 0;

  // Trocou a pessoa: volta ao select
  useEffect(() => { setShowForm(false); }, [patientId]);

  const selected = cards.find(c => String(c.id) === String(value)) || null;

  // Com o formulário aberto nada fica escolhido: salvar o agendamento pede a
  // carteirinha nova em vez de usar a anterior sem avisar. Cancelar devolve a anterior.
  const openForm = () => {
    previousValue.current = value;
    onChange?.(null);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    onChange?.(previousValue.current);
  };

  const handleSaved = (card) => {
    onCardCreated?.(card);
    if (card?.id != null) onChange?.(card.id);
    setShowForm(false);
  };

  if (loading && !hasCards) {
    return <div className={styles.loading}>Carregando carteirinhas...</div>;
  }

  const form = (
    <div className={styles.formWrapper}>
      <PatientInsuranceForm
        patientId={patientId}
        providers={providers}
        compact
        onSaved={handleSaved}
        onCancel={hasCards ? cancelForm : undefined}
      />
    </div>
  );

  if (!hasCards) {
    return (
      <div className={styles.picker}>
        <span className={styles.hint}>Esta pessoa ainda não tem carteirinha. Cadastre aqui mesmo.</span>
        {form}
        {error && <span className={drawerStyles.validationError}>{error}</span>}
      </div>
    );
  }

  return (
    <div className={styles.picker}>
      <div className={styles.headerRow}>
        <label className={drawerStyles.fieldLabel}>Carteirinha</label>
        {!showForm && (
          <button type="button" className={styles.linkBtn} onClick={openForm} disabled={disabled}>
            Nova carteirinha
          </button>
        )}
      </div>
      {showForm ? form : (
        <select
          className={drawerStyles.fieldSelect}
          value={value != null ? String(value) : ''}
          onChange={e => onChange?.(e.target.value ? e.target.value : null)}
          disabled={disabled}
          aria-invalid={Boolean(error)}
        >
          <option value="">Selecionar...</option>
          {cards.map(card => (
            <option key={card.id} value={String(card.id)}>
              {card.label || insuranceCardLabel(card)}{card.expired ? ' (vencida)' : ''}
            </option>
          ))}
        </select>
      )}
      {selected?.expired && !showForm && (
        <div className={styles.warning}>
          <span className={styles.warningBadge}>Vencida</span>
          <span>
            {selected.valid_until
              ? `${selected.expired ? 'Venceu em' : 'Vence em'} ${formatDay(selected.valid_until)}. `
              : ''}
            Confira com a operadora antes do atendimento.
          </span>
        </div>
      )}
      {error && <span className={drawerStyles.validationError}>{error}</span>}
    </div>
  );
}
